// Copyright 2016 Sorama B.V.

import {
    Region,
    Resolution,
    StreamBeamformingProjection,
    StreamBeamformingProjectionProperties,
    StreamHologram,
    StreamHologramProperties,
    StreamSpectrum,
    StreamSpectrumProperties,
} from "datatypes/SoramaLocalApi";
import { Status } from "datatypes/Status";

export abstract class IMeasurementPreviewService {
    /**
     * Indicates whether the hologram preview function can be used
     */
    public isHologramAvailable: Status;

    /**
     * Gets a hologram in Pascal RMS with the given properties
     * Requires the 'isHologramAvailable' property to be 'Available'
     * @param properties Properties of the hologram to get
     */
    public abstract previewHologram(properties: StreamHologramProperties): Promise<StreamHologram>;

    /**
     * Indicates whether the beamforming projection preview function can be used
     */
    public isBeamformingProjectionAvailable: Status;

    /**
     * Gets a beamforming projection in Pascal RMS with the given properties
     * Requires the 'isBeamformingProjectionAvailable' property to be 'Available'
     * @param properties Properties of the beamforming projection to get
     */
    public abstract previewBeamformingProjection(
        properties: StreamBeamformingProjectionProperties
    ): Promise<StreamBeamformingProjection>;

    /**
     * Indicates whether the spectrum preview function can be used
     */
    public isSpectrumAvailable: Status;

    /**
     * Gets a audio spectrum in Pascal RMS
     * Requires the 'isSpectrumAvailable' property to be 'Available'
     */
    public abstract previewSpectrum(properties?: StreamSpectrumProperties): Promise<StreamSpectrum>;

    //#region Video Preview Functions

    /**
     * Indicates whether the video preview functions can be used
     */
    public isVideoAvailable: Status;

    /**
     * Gets the resolution for the video device
     * Requires the 'isVideoAvailable' property to be 'Available'
     */
    public abstract getResolution(): Promise<Resolution>;

    /**
     * Gets a base64 encoded JPEG image from the video device
     * Requires the 'isVideoAvailable' property to be 'Available'
     */
    public abstract previewImage(): Promise<string>;

    /**
     * Gets the stream address for the video device
     * Requires the 'isVideoAvailable' property to be 'Available'
     */
    public abstract getStreamAddress(): Promise<string>;

    /**
     * Gets the visible region for the video device with respect to the center of the video device
     * Requires the 'isVideoAvailable' property to be 'Available'
     */
    public abstract getVisibleRegion(distance: number): Promise<Region>;

    //#endregion
}
